import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, Bluetooth, Shield, SunMoon } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { useToast } from '../contexts/ToastContext';
import { clearAllQueryCache } from '../lib/query/clearQueryCache';
import DeleteAccountModal from './DeleteAccountModal';
import Modal, { ModalFooter } from './ui/Modal';
import Button from './ui/Button';
import { startWelcome, welcomeAvailable } from '../lib/welcomeDemo';
import { forgetPushToken, pushPermission, pushSupported, requestPush } from '../lib/push';
import { GLASS_SURFACES, CARD_SOLID_SURFACE_CLASS } from '../config/glassSurfaces';
import { useIsMapOperator } from '../hooks/queries/useIsMapOperator';
import { useBeaconMode } from '../hooks/useBeaconMode';
import { setBeaconMode } from '../lib/proximity/beaconPrivacy';
import { palettePref, setPalettePref } from '../lib/map3d/mapPalette';

import { usePromptAuth } from './auth/AuthPrompt';
export default function Settings() {
  const navigate = useNavigate();
  const promptAuth = usePromptAuth();
  const queryClient = useQueryClient();
  const { session } = useCurrentUserProfile();
  const { showToast } = useToast();
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  // Find testing is for operators only (the people QAing Find at shows), never shown to everyone else.
  const { data: isOperator } = useIsMapOperator();
  const beaconMode = useBeaconMode();
  const [mapLook, setMapLook] = useState(palettePref);

  // Fetch user preferences
  useEffect(() => {
    if (!session?.user?.id) {
      setLoading(false);
      return;
    }

    async function fetchPreferences() {
      setLoading(true);

      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching preferences:', error);
      }

      if (data) {
        setPreferences(data);
      } else {
        // Create default preferences
        const { data: newPrefs, error: insertError } = await supabase
          .from('user_preferences')
          .insert({ user_id: session.user.id })
          .select()
          .single();

        if (!insertError && newPrefs) {
          setPreferences(newPrefs);
        }
      }

      setLoading(false);
    }

    fetchPreferences();
  }, [session?.user?.id]);

  const handleToggle = async (key, value) => {
    if (!preferences) return;

    setSaving(true);
    const newValue = !value;

    // Optimistic update
    setPreferences((prev) => ({ ...prev, [key]: newValue }));

    const { error } = await supabase
      .from('user_preferences')
      .update({ [key]: newValue })
      .eq('user_id', session.user.id);

    if (error) {
      // Revert on error
      setPreferences((prev) => ({ ...prev, [key]: value }));
      console.error('Error updating preference:', error);
    }

    setSaving(false);
  };

  const handleLogout = async () => {
    await forgetPushToken(); // while still signed in: this phone stops getting this account's pushes
    clearAllQueryCache(queryClient, session?.user?.id ?? null);
    await supabase.auth.signOut();
    // Clear auth-related storage only; preserve theme preference
    Object.keys(localStorage).forEach((key) => {
      if (key !== 'prii-theme') localStorage.removeItem(key);
    });
    sessionStorage.clear();
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    try {
      // Call the database function to delete user completely
      const { error } = await supabase.rpc('delete_user_account');

      if (error) {
        console.error('Error deleting account:', error);
        // Fallback: try deleting profile manually if function doesn't exist
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) {
          const { error: profileError } = await supabase
            .from('profiles')
            .delete()
            .eq('id', user.id);

          if (profileError) {
            throw profileError;
          }
        }
      }

      // No need to sign out - user is already deleted from auth.users
      // The session is invalid anyway, so just clear local data and redirect
      // Clear auth-related storage only; preserve theme preference
      Object.keys(localStorage).forEach((key) => {
        if (key !== 'prii-theme') localStorage.removeItem(key);
      });
      sessionStorage.clear();

      // Redirect to home
      window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account:', error);
      showToast('Failed to delete account. Please try again.');
    }
  };

  if (!session) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <Shield className="w-16 h-16 mx-auto text-gray-300 dark:text-dark-border mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-2">Settings</h2>
        <p className="text-gray-500 dark:text-dark-muted mb-6">Log in to manage your settings</p>
        <Button size="lg" onClick={() => promptAuth()}>
          Log In
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-6">Settings</h1>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className={`overflow-hidden rounded-xl animate-pulse p-6 ${
                GLASS_SURFACES.cards ? 'event-card-surface' : CARD_SOLID_SURFACE_CLASS
              }`}
              aria-hidden
            >
              <div className="space-y-3">
                <div className="h-5 w-48 rounded skeleton-shimmer" />
                <div className="h-4 w-full max-w-md rounded skeleton-shimmer" />
                <div className="h-10 w-full rounded-lg skeleton-shimmer" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Notification Settings */}
          <section
            className={`overflow-hidden rounded-xl ${
              GLASS_SURFACES.cards ? 'event-card-surface' : CARD_SOLID_SURFACE_CLASS
            }`}
          >
            <div className="border-b border-gray-100 px-6 py-4 dark:border-dark-border/70">
              <h2 className="flex items-center gap-2 font-semibold text-gray-900 dark:text-dark-text glass-surface-text-emphasis">
                <Bell className="h-5 w-5 text-primary" />
                Notification Settings
              </h2>
            </div>

            <div className="divide-y divide-gray-100 dark:divide-dark-border/70">
              {pushSupported() && <PushStatusRow />}
              <SettingToggle
                label="Group Invites"
                description="Get notified when you're added to a group"
                value={preferences?.notify_group_invites}
                onChange={() =>
                  handleToggle('notify_group_invites', preferences?.notify_group_invites)
                }
                disabled={saving}
              />
              <SettingToggle
                label="Join Requests"
                description="Get notified when someone requests to join your group"
                value={preferences?.notify_join_requests}
                onChange={() =>
                  handleToggle('notify_join_requests', preferences?.notify_join_requests)
                }
                disabled={saving}
              />
              <SettingToggle
                label="New Followers"
                description="Get notified when someone follows you"
                value={preferences?.notify_follows ?? true}
                onChange={() => handleToggle('notify_follows', preferences?.notify_follows ?? true)}
                disabled={saving}
              />
              <SettingToggle
                label="Event Updates"
                description="Get notified about lineup changes, cancellations, etc."
                value={preferences?.notify_event_updates}
                onChange={() =>
                  handleToggle('notify_event_updates', preferences?.notify_event_updates)
                }
                disabled={saving}
              />
            </div>
          </section>

          {isOperator === true && (
            <section
              className={`overflow-hidden rounded-xl ${
                GLASS_SURFACES.cards ? 'event-card-surface' : CARD_SOLID_SURFACE_CLASS
              }`}
            >
              <div className="border-b border-gray-100 px-6 py-4 dark:border-dark-border/70">
                <h2 className="flex items-center gap-2 font-semibold text-gray-900 dark:text-dark-text glass-surface-text-emphasis">
                  <Bluetooth className="h-5 w-5 text-primary" />
                  Find beacon
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-dark-muted">
                  This phone only. Find restarts with it.
                </p>
              </div>
              <div
                role="radiogroup"
                aria-label="Find beacon"
                className="divide-y divide-gray-100 dark:divide-dark-border/70"
              >
                {BEACON_MODES.map((m) => (
                  <SettingChoice
                    key={m.value}
                    label={m.label}
                    description={m.description}
                    selected={beaconMode === m.value}
                    onSelect={() => {
                      if (beaconMode === m.value) return;
                      setBeaconMode(m.value);
                      showToast(`Find beacon: ${m.label}`);
                    }}
                  />
                ))}
              </div>
            </section>
          )}

          {isOperator === true && (
            <section
              className={`overflow-hidden rounded-xl ${
                GLASS_SURFACES.cards ? 'event-card-surface' : CARD_SOLID_SURFACE_CLASS
              }`}
            >
              <div className="border-b border-gray-100 px-6 py-4 dark:border-dark-border/70">
                <h2 className="flex items-center gap-2 font-semibold text-gray-900 dark:text-dark-text glass-surface-text-emphasis">
                  <SunMoon className="h-5 w-5 text-primary" />
                  Map look
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-dark-muted">
                  This phone only. The next map you open uses it.
                </p>
              </div>
              <div
                role="radiogroup"
                aria-label="Map look"
                className="divide-y divide-gray-100 dark:divide-dark-border/70"
              >
                {MAP_LOOKS.map((m) => (
                  <SettingChoice
                    key={m.value}
                    label={m.label}
                    description={m.description}
                    selected={mapLook === m.value}
                    onSelect={() => {
                      if (mapLook === m.value) return;
                      setPalettePref(m.value);
                      setMapLook(m.value);
                      showToast(`Map look: ${m.label}`);
                    }}
                  />
                ))}
              </div>
            </section>
          )}

          <section className="pt-2">
            {/* The welcome flow (Stage 10) on demand - it only starts by itself for a new account. It replaced the three-card
                intro (Sam, 2026-10-27). */}
            {welcomeAvailable() && (
              <Button
                type="button"
                variant="secondary"
                fullWidth
                onClick={() => startWelcome(navigate, '/settings')}
                className="mb-2 border-gray-200 dark:border-dark-border text-gray-600 dark:text-dark-muted py-3 !h-auto min-h-[2.75rem] hover:bg-gray-100 dark:hover:bg-dark-bg"
              >
                Show welcome steps
              </Button>
            )}
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => setShowLogoutModal(true)}
              className="border-gray-200 dark:border-dark-border text-gray-600 dark:text-dark-muted py-3 !h-auto min-h-[2.75rem] hover:bg-gray-100 dark:hover:bg-dark-bg"
            >
              Log Out
            </Button>
          </section>

          {/* Delete Account Section */}
          <section className="pt-1">
            <div className="p-0">
              <p className="text-sm text-gray-500 dark:text-dark-muted mb-3">
                Once you delete your account, there is no going back. All your data will be
                permanently deleted.
              </p>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowDeleteModal(true)}
                className="!h-auto px-0 py-0 font-semibold text-gray-500 dark:text-dark-muted hover:bg-transparent hover:text-gray-600 dark:hover:text-dark-text"
              >
                Delete Account
              </Button>
            </div>
          </section>

          {/* The legal pages, at the foot of Settings where apps keep them (Sam, 2026-10-27). */}
          <nav
            aria-label="Legal"
            className="flex justify-center gap-4 pt-2 text-xs text-gray-500 dark:text-dark-muted"
          >
            <Link to="/terms" className="hover:text-gray-700 dark:hover:text-dark-text">
              Terms
            </Link>
            <span aria-hidden="true">·</span>
            <Link to="/privacy" className="hover:text-gray-700 dark:hover:text-dark-text">
              Privacy
            </Link>
            <span aria-hidden="true">·</span>
            <a
              href="mailto:samburtch9@gmail.com"
              className="hover:text-gray-700 dark:hover:text-dark-text"
            >
              Contact
            </a>
          </nav>
        </div>
      )}

      <Modal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        title="Log out of Prii?"
        size="sm"
      >
        <ModalFooter align="split">
          <Button variant="secondary" onClick={() => setShowLogoutModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              setShowLogoutModal(false);
              await handleLogout();
            }}
          >
            Log Out
          </Button>
        </ModalFooter>
      </Modal>

      {showDeleteModal && (
        <DeleteAccountModal
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteAccount}
        />
      )}
    </div>
  );
}

/** Find beacon modes (docs/beacon-privacy.md). Shadow is the default. */
// Map palettes on trial (lib/map3d/mapPalette.js): auto by the sun at the venue, or one forced for testing outside.
const MAP_LOOKS = [
  {
    value: 'auto',
    label: 'Auto',
    description: 'Day, then Dusk the last hour before sunset, then Night.',
  },
  { value: 'day', label: 'Day', description: 'Bright, for full sun.' },
  { value: 'dusk', label: 'Dusk', description: 'Darker green and lavender.' },
  { value: 'night', label: 'Night', description: 'Dark violet ground.' },
  { value: 'today', label: 'Original', description: 'The map before the trial.' },
];

const BEACON_MODES = [
  {
    value: 'shadow',
    label: 'Shadow test',
    description: "Today's beacon, plus the private one checked in the background and logged.",
  },
  {
    value: 'on',
    label: 'Private',
    description: "Private beacon only. Older builds won't see this phone over Bluetooth.",
  },
  { value: 'off', label: 'Standard', description: "Today's beacon only." },
];

function SettingChoice({ label, description, selected, onSelect }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className="flex w-full items-center justify-between px-6 py-4 text-left"
    >
      <span className="flex-1 min-w-0 pr-4">
        <span className="block font-medium text-gray-900 dark:text-dark-text">{label}</span>
        <span className="mt-0.5 block text-sm text-gray-500 dark:text-dark-muted">
          {description}
        </span>
      </span>
      <span
        aria-hidden="true"
        className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${
          selected ? 'border-primary' : 'border-gray-300 dark:border-dark-border'
        }`}
      >
        {selected && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
      </span>
    </button>
  );
}

/** The phone's own push permission, over the per-type switches (round 6): iOS asks only once, so a no is turned back on
 * in iOS Settings, and the row says where. */
function PushStatusRow() {
  const [perm, setPerm] = useState(null);
  useEffect(() => {
    void pushPermission().then(setPerm);
  }, []);
  if (!perm || perm === 'unsupported') return null;
  const on = perm === 'granted';
  const denied = perm === 'denied';
  return (
    <div className="flex items-center justify-between px-6 py-4">
      <div className="flex-1 min-w-0 pr-4">
        <p className="font-medium text-gray-900 dark:text-dark-text">Push notifications</p>
        <p className="text-sm text-gray-500 dark:text-dark-muted mt-0.5">
          {on
            ? 'On for this phone'
            : denied
              ? 'Turn on notifications for Prii in iOS Settings'
              : 'Off for this phone'}
        </p>
      </div>
      {!on && !denied && (
        <Button
          type="button"
          size="sm"
          className="!rounded-full !px-4"
          onClick={async () => setPerm(await requestPush())}
        >
          Turn on
        </Button>
      )}
    </div>
  );
}

function SettingToggle({ label, description, value, onChange, disabled }) {
  return (
    <div className="flex items-center justify-between px-6 py-4">
      <div className="flex-1 min-w-0 pr-4">
        <p className="font-medium text-gray-900 dark:text-dark-text">{label}</p>
        {description && (
          <p className="text-sm text-gray-500 dark:text-dark-muted mt-0.5">{description}</p>
        )}
      </div>
      <div className="flex-shrink-0 flex items-center self-center">
        {/* The app's glass switch: frosted track; on, the Join button's ring, fill and glow. */}
        <button
          type="button"
          role="switch"
          aria-checked={Boolean(value)}
          aria-label={label}
          onClick={onChange}
          disabled={disabled}
          className={`glass-switch${value ? ' glass-switch--on' : ''}`}
        >
          <span className="glass-switch__knob" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
